import { useContext, useState } from 'react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import config from 'src/config';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { Switch } from 'src/components/ui/switch';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import type { FamousOrderConfirmationDTO } from '@erp/rpc';
import type useSendEdi855Transaction from 'src/api/temp/famous/useSendEdi855Transaction';
import { T, Var, useGT } from 'gt-react';

export interface SendEdi855ButtonProps {
  orderConfirmation: FamousOrderConfirmationDTO;
  sendEdi855Transaction: ReturnType<typeof useSendEdi855Transaction>;
  onSendEdi: (force?: boolean) => void;
  onRefreshStatus: () => void;
  isLoading: boolean;
}

function SendEdi855Button({
  orderConfirmation,
  sendEdi855Transaction,
  onSendEdi,
  onRefreshStatus,
  isLoading,
}: SendEdi855ButtonProps) {
  const gt = useGT();
  const sentAt = orderConfirmation.edi855SentAt;
  const statusRaw = orderConfirmation.edi855Status;
  const isError = statusRaw === 'error';
  const isPending = statusRaw === 'pending';
  const isSent = statusRaw === 'sent';
  const transactionId = orderConfirmation.edi855TransactionId;
  const errorMessage = orderConfirmation.edi855Error;
  const isDevelopment = config.isDev;
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const userIsAdmin = isAdmin(currentUser, viewMode);
  const [useTestStream, setUseTestStream] = useState(false);

  /**
   * A test send is driven from here rather than through `onSendEdi`, and its
   * result is derived from the mutation rather than stored: nothing is persisted
   * for a test, so the mutation's own `variables`/`data` are the whole record,
   * and threading state back up through the modal would buy nothing.
   */
  function sendOnTestStream() {
    sendEdi855Transaction.mutate({
      orderConfirmationId: orderConfirmation.id,
      // A test writes no status, so there is no already-sent record for the
      // backend's guard to protect.
      force: true,
      test: true,
    });
  }

  const testSendTransactionId = sendEdi855Transaction.variables?.test
    ? (sendEdi855Transaction.data?.transactionId ?? null)
    : null;

  function renderSentStatusUi() {
    return (
      <MDBox>
        <MDBox
          sx={{
            p: 2,
            backgroundColor: '#e8f5e8',
            borderRadius: 1,
            border: '1px solid #c8e6c9',
            mb: 2,
          }}
        >
          <MDTypography
            variant="body2"
            fontWeight="bold"
            color="success.main"
            mb={1}
          >
            <T>EDI 855 Sent</T>
          </MDTypography>
          <MDTypography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            <T>
              Sent:{' '}
              <Var>{sentAt ? new Date(sentAt).toLocaleDateString() : ''}</Var>{' '}
              at{' '}
              <Var>
                {sentAt
                  ? new Date(sentAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : ''}
              </Var>
            </T>
          </MDTypography>
          {transactionId && (
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              <T>
                Transaction ID: <Var>{transactionId}</Var>
              </T>
            </MDTypography>
          )}
        </MDBox>

        {transactionId && (
          <MDButton
            variant="outlined"
            color="primary"
            onClick={() =>
              window.open(
                `https://ui.orderful.com/transactions/${transactionId}`,
                '_blank',
                'noopener,noreferrer',
              )
            }
            fullWidth
            sx={{ mb: 2 }}
          >
            <T>View in Orderful</T>
          </MDButton>
        )}
      </MDBox>
    );
  }

  function renderErrorStatusUi() {
    return (
      <MDBox
        sx={{
          p: 2,
          backgroundColor: '#ffebee',
          borderRadius: 1,
          border: '1px solid #ffcdd2',
          mb: 2,
        }}
      >
        <MDTypography
          variant="body2"
          fontWeight="bold"
          color="error.main"
          mb={1}
        >
          <T>EDI 855 Failed</T>
        </MDTypography>
        {sentAt && (
          <MDTypography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            <T>
              Last attempt: <Var>{new Date(sentAt).toLocaleDateString()}</Var>{' '}
              at{' '}
              <Var>
                {new Date(sentAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Var>
            </T>
          </MDTypography>
        )}
        {errorMessage && (
          <MDTypography
            variant="caption"
            color="error"
            sx={{ display: 'block' }}
          >
            {errorMessage}
          </MDTypography>
        )}
      </MDBox>
    );
  }

  function renderPendingStatusUi() {
    return (
      <MDBox>
        <MDBox
          sx={{
            p: 2,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #90caf9',
            mb: 2,
          }}
        >
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <MDTypography variant="body2" fontWeight="bold" color="info.main">
              <T>EDI 855 Pending</T>
            </MDTypography>
            <MDButton
              variant="text"
              color="dark"
              onClick={onRefreshStatus}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              <ArrowsClockwiseIcon size={18} />
            </MDButton>
          </MDBox>
          {sentAt && (
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              <T>
                Submitted: <Var>{new Date(sentAt).toLocaleDateString()}</Var> at{' '}
                <Var>
                  {new Date(sentAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Var>
              </T>
            </MDTypography>
          )}
          {transactionId && (
            <MDTypography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              <T>
                Transaction ID: <Var>{transactionId}</Var>
              </T>
            </MDTypography>
          )}
        </MDBox>

        {transactionId && (
          <MDButton
            variant="outlined"
            color="info"
            onClick={() =>
              window.open(
                `https://ui.orderful.com/transactions/${transactionId}`,
                '_blank',
                'noopener,noreferrer',
              )
            }
            fullWidth
            sx={{ mb: 2 }}
          >
            <T>View in Orderful</T>
          </MDButton>
        )}
      </MDBox>
    );
  }

  function renderSendButtonUi(forceResend = false) {
    return (
      <MDBox>
        <MDButton
          variant="contained"
          color={forceResend ? 'warning' : 'primary'}
          // The toggle decides the stream for whatever send is on screen.
          onClick={() =>
            useTestStream ? sendOnTestStream() : onSendEdi(forceResend)
          }
          disabled={sendEdi855Transaction.isPending || isLoading}
          fullWidth
          sx={{ mb: 2 }}
        >
          {sendEdi855Transaction.isPending
            ? gt('Sending...', {
                context: 'button label while EDI 855 transaction is being sent',
              })
            : forceResend
              ? gt('Resend EDI 855 Transaction', {
                  context:
                    'button label to force-resend an EDI 855 transaction',
                })
              : isError
                ? gt('Retry EDI 855 Transaction', {
                    context:
                      'button label to retry a failed EDI 855 transaction',
                  })
                : gt('Send EDI 855 Transaction', {
                    context: 'button label to send an EDI 855 transaction',
                  })}
        </MDButton>

        {sendEdi855Transaction.error && (
          <MDBox
            sx={{
              p: 2,
              backgroundColor: '#ffebee',
              borderRadius: 1,
              border: '1px solid #ffcdd2',
            }}
          >
            <MDTypography variant="caption" color="error">
              <T>
                Error:{' '}
                <Var>
                  {sendEdi855Transaction.error?.message ||
                    gt('Failed to send EDI 855 transaction')}
                </Var>
              </T>
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    );
  }

  function renderDevelopmentUi() {
    if (isSent) {
      // Match the production admin gate — the backend rejects `force=true`
      // from non-admins with FORBIDDEN, so showing the button to a
      // non-admin dev user would just produce a broken click.
      if (!userIsAdmin) {
        return renderSentStatusUi();
      }
      return (
        <MDBox>
          {renderSentStatusUi()}
          <AlertContainer className="mb-2 border-amber-200 bg-amber-50">
            <AlertDescription className="text-amber-800">
              <T>
                Development mode: You can resend the EDI 855 transaction. This
                will create a new transaction in Orderful.
              </T>
            </AlertDescription>
          </AlertContainer>
          {renderSendButtonUi(true)}
        </MDBox>
      );
    }
    if (isPending) return renderPendingStatusUi();
    if (isError) {
      return (
        <MDBox>
          {renderErrorStatusUi()}
          {renderSendButtonUi(false)}
        </MDBox>
      );
    }
    return renderSendButtonUi(false);
  }

  function renderProductionUi() {
    if (isSent) {
      // Admins can force-resend a 855 that appears "sent" but actually
      // failed downstream (e.g. Orderful accepted the POST but delivery
      // was rejected). Non-admins only see the sent status.
      if (userIsAdmin) {
        return (
          <MDBox>
            {renderSentStatusUi()}
            <AlertContainer className="mb-2 border-amber-200 bg-amber-50">
              <AlertDescription className="text-amber-800">
                <T>
                  Admin: this will create a new transaction in Orderful. Only
                  use this if the 855 was not actually delivered.
                </T>
              </AlertDescription>
            </AlertContainer>
            {renderSendButtonUi(true)}
          </MDBox>
        );
      }
      return renderSentStatusUi();
    }
    if (isPending) return renderPendingStatusUi();
    if (isError) {
      return (
        <MDBox>
          {renderErrorStatusUi()}
          {renderSendButtonUi(false)}
        </MDBox>
      );
    }
    return renderSendButtonUi();
  }

  /**
   * Picks the stream for whichever send button is on screen. When on, the 855
   * goes to Orderful's TEST stream and nothing is written to this confirmation's
   * status, so a real acknowledgment can be rehearsed on a LIVE deployment
   * without reaching the trading partner or disturbing the record.
   */
  function renderTestStreamToggle() {
    return (
      <div className="mb-2 flex items-center gap-2">
        <Switch
          id="edi-855-test-stream"
          checked={useTestStream}
          onCheckedChange={setUseTestStream}
          disabled={sendEdi855Transaction.isPending}
        />
        <label
          htmlFor="edi-855-test-stream"
          className="cursor-pointer text-sm text-gray-700"
        >
          <T context="toggle label to send the EDI 855 on the Orderful TEST stream">
            Send on TEST stream
          </T>
        </label>
      </div>
    );
  }

  /**
   * Send button for the states whose normal UI offers none — already sent, or
   * mid-send. That is the most useful moment to rehearse a send, and without
   * this the toggle would have nothing to act on there.
   */
  function renderTestStreamSendButton() {
    return (
      <MDButton
        variant="outlined"
        color="secondary"
        onClick={sendOnTestStream}
        disabled={sendEdi855Transaction.isPending || isLoading}
        fullWidth
        sx={{ mb: 2 }}
      >
        {sendEdi855Transaction.isPending
          ? gt('Sending on TEST...', {
              context: 'button label while a test EDI 855 is being sent',
            })
          : gt('Send EDI 855 on TEST stream', {
              context:
                'button label to send an EDI 855 on the Orderful TEST stream',
            })}
      </MDButton>
    );
  }

  /**
   * Result of the last test send — the transaction id is the only trace one
   * leaves, since nothing is written to the confirmation.
   *
   * Deliberately not shared with the 856 modal's equivalent: factoring the two
   * together would pull a 1,700-line component into this change for a nine-line
   * panel. An anchor rather than a scripted `window.open` — same destination,
   * middle-clickable.
   */
  function renderTestSendResult() {
    return (
      <AlertContainer className="mb-2 border-blue-200 bg-blue-50">
        <AlertDescription className="text-blue-900 text-sm">
          <T>
            Test transaction sent on the TEST stream:{' '}
            <Var>{testSendTransactionId}</Var>. Nothing was recorded against
            this order confirmation.
          </T>{' '}
          <a
            className="underline"
            href={`https://ui.orderful.com/transactions/${testSendTransactionId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <T context="link to view the test EDI transaction in Orderful">
              View in Orderful
            </T>
          </a>
        </AlertDescription>
      </AlertContainer>
    );
  }

  return (
    <MDBox>
      {userIsAdmin && renderTestStreamToggle()}
      {isDevelopment ? renderDevelopmentUi() : renderProductionUi()}
      {testSendTransactionId && renderTestSendResult()}
      {userIsAdmin &&
        useTestStream &&
        (isSent || isPending) &&
        renderTestStreamSendButton()}
    </MDBox>
  );
}

export default SendEdi855Button;
